// The one place the SPA talks to the PMS API over HTTP. Every request joins the
// base URL from src/config.ts (CLAUDE.md #6), sends/receives the PMS `{ error }`
// envelope, and runs with `credentials: "include"` so the httpOnly broker
// refresh cookie (set by PMS on the sibling subdomain) is stored and replayed.
//
// Failures surface as a typed `ApiError` the UI can branch on by `status`
// (CLAUDE.md #7) — including network errors as `status: 0` — so a bad response
// never becomes an unhandled throw that white-screens the app.

import { getConfig } from "@/config";

/** A handled API failure: the PMS error message + the HTTP status (0 = network).
 *  `body` is the decoded error envelope when present, so a caller that needs more
 *  than the message (e.g. the de-dup 409's `candidates`, BRK-S020) can read it. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export interface RequestOptions {
  method?: HttpMethod;
  /** JSON-serialized as the request body when present. */
  body?: unknown;
  /** Sent as `Authorization: Bearer <token>` when present. */
  accessToken?: string | null;
  signal?: AbortSignal;
}

/** A decoded 2xx response paired with its HTTP status, for the rare caller that
 *  must distinguish e.g. 201 (created) from 200 (attached) — BRK-S020. */
export interface ApiResult<T> {
  status: number;
  data: T;
}

/**
 * The enumerated `details.code` a structured PMS refusal carries, or null.
 *
 * ONE reading of where the code lives, shared by the field-error placement
 * (`serverFieldErrors.ts`) and the seam's own status checks (`brokerBonds.ts`
 * — which must tell a double submit from an ORDER_CANCELLED 409, BRK-S181).
 */
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (typeof body !== "object" || body === null) return null;
  const details = (body as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return null;
  const code = (details as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

function isErrorEnvelope(value: unknown): value is { error: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as { error: unknown }).error === "string"
  );
}

/**
 * Issue a request to the PMS API and parse its JSON body. Resolves with the
 * decoded body on a 2xx, throws `ApiError` otherwise. `204 No Content` (and any
 * empty body) resolves to `undefined`.
 */
export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return (await apiRequestWithStatus<T>(path, options)).data;
}

/**
 * Issue a GET and resolve its BYTES.
 *
 * The one non-JSON read this portal makes (BRK-S133: the Program Indication
 * letter). It exists because `apiRequest` cannot do it — that reads the body as
 * text and `JSON.parse`s it, so a PDF throws on the first byte — and because a
 * plain `<iframe src>` cannot either: the bytes are behind an own-scoped,
 * bearer-authenticated route, and an iframe's own request carries no
 * `Authorization` header. So the SPA fetches the blob itself and hands the
 * viewer an object URL.
 *
 * A FAILURE STILL DECODES THE `{error}` ENVELOPE. The error path is JSON even
 * when the success path is not, so a 404 for a letter that does not exist yet
 * reaches the UI as PMS's own sentence rather than as "Request failed (404)".
 */
export interface FileResponse {
  blob: Blob;
  /**
   * The SERVER's filename, from `Content-Disposition`, or null when it sent
   * none.
   *
   * Carried rather than invented, because the server is what names the
   * document: PMS's indication letter arrives as
   * `Program-Indication-SB-01578.pdf`, which is meaningful to a broker and
   * carries the order number. A client-side name built from an id would
   * replace that with a uuid.
   */
  filename: string | null;
}

/**
 * The `filename` from a `Content-Disposition` header, or null.
 *
 * Prefers RFC 5987's `filename*` (percent-encoded, charset-tagged) over the
 * plain `filename`, because that is the one that survives a non-ASCII name —
 * and falls back when it is absent or undecodable. Any path separators are
 * stripped: a filename is a name, and honouring `../` from a header would let
 * a response choose where a download lands.
 */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      const safe = decoded.split(/[/\\]/).pop()?.trim();
      if (safe) return safe;
    } catch {
      // Undecodable — fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);
  const raw = (plain?.[1] ?? plain?.[2] ?? "").trim();
  const safe = raw.split(/[/\\]/).pop()?.trim();
  return safe ? safe : null;
}

export async function apiRequestBlob(
  path: string,
  options: Omit<RequestOptions, "body" | "method"> = {},
): Promise<FileResponse> {
  const { accessToken, signal } = options;
  const headers: Record<string, string> = {};
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(`${getConfig().pmsApiBaseUrl}${path}`, {
      method: "GET",
      headers,
      credentials: "include",
      signal,
    });
  } catch {
    throw new ApiError(
      0,
      "Network error — check your connection and try again.",
    );
  }

  if (!response.ok) {
    // Read as TEXT first: an error body is JSON, and `.json()` on an empty or
    // non-JSON error response would throw past the handled `ApiError` this
    // function promises (NN #7).
    const text = await response.text().catch(() => "");
    let data: unknown;
    try {
      data = text ? JSON.parse(text) : undefined;
    } catch {
      data = undefined;
    }
    const message = isErrorEnvelope(data)
      ? data.error
      : `Request failed (${response.status})`;
    throw new ApiError(response.status, message, data);
  }

  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(
      response.headers.get("Content-Disposition"),
    ),
  };
}

/**
 * POST a FILE to the API, as `multipart/form-data`.
 *
 * The one write this portal makes that is not JSON (BRK-S141: the final
 * contract on a won bid). `apiRequest` cannot do it — it `JSON.stringify`s the
 * body, which turns a `File` into `{}` — and the `Content-Type` is deliberately
 * NOT set here: the browser must write it itself, because a multipart body is
 * unparseable without the boundary token only `fetch` knows.
 *
 * Note the difference from the INTAKE upload path (BRK-S038), which is three
 * calls: a presigned ticket, a `PUT` straight to storage, then a confirm. That
 * shape exists because an intake packet can be large and goes to object storage
 * without touching the app tier. This one is a single small document posted to
 * a route, which is what the story brief describes — see
 * `uploadBondFinalContract` for why that convention is not yet verified against
 * the contract.
 */
export async function apiRequestUpload<T>(
  path: string,
  form: FormData,
  options: Omit<RequestOptions, "body" | "method"> = {},
): Promise<T> {
  const { accessToken, signal } = options;
  const headers: Record<string, string> = {};
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(`${getConfig().pmsApiBaseUrl}${path}`, {
      method: "POST",
      headers,
      body: form,
      credentials: "include",
      signal,
    });
  } catch {
    throw new ApiError(
      0,
      "Network error — check your connection and try again.",
    );
  }

  // Read as TEXT first: an empty or non-JSON body must not throw past the
  // handled `ApiError` this promises (NN #7).
  return decodeApiBody<T>(response.status, response.ok, await response.text());
}

/**
 * A response's body as `T`, or a thrown `ApiError` carrying PMS's own sentence.
 *
 * Shared by the two upload paths below, which is the only reason it exists: an
 * error envelope decoded two ways eventually decodes it two ways, and the
 * `text`-first rule (never `.json()` on a body that may be empty or HTML) is
 * exactly the kind of care that gets dropped in a copy.
 */
function decodeApiBody<T>(status: number, ok: boolean, text: string): T {
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!ok) {
    const message = isErrorEnvelope(data)
      ? data.error
      : `Request failed (${status})`;
    throw new ApiError(status, message, data);
  }
  return data as T;
}

/** How far one file's upload has got. `total` is 0 when the browser cannot say
 *  (a non-`lengthComputable` progress event), which reads as "unknown". */
export interface UploadProgressEvent {
  loaded: number;
  total: number;
}

export interface UploadWithProgressOptions {
  accessToken?: string | null;
  signal?: AbortSignal;
  onProgress?: (progress: UploadProgressEvent) => void;
}

/**
 * POST a `multipart/form-data` body and report the upload's PROGRESS.
 *
 * `XMLHttpRequest`, not `fetch`, and that is the whole reason this exists
 * beside `apiRequestUpload`: `fetch` cannot report upload progress
 * cross-browser (a request-body `ReadableStream` is not portable), and a
 * broker watching a 9 MB scan needs to see it move. The same argument
 * `uploadToPresigned` makes for the intake path (BRK-S038).
 *
 * TWO UPLOADERS IS DELIBERATE, and this is the reason to keep them: the
 * final-contract upload (BRK-S141) is one small PDF whose response is the
 * whole interaction, and its tests pin the `fetch` call and the absence of a
 * hand-set `Content-Type`. Rewriting that path onto XHR to save a function
 * would be churn on a shipped surface for no behaviour a broker sees. What must
 * NOT diverge is the error handling, so both decode through `decodeApiBody`.
 *
 * `Content-Type` is again deliberately unset: only the browser knows the
 * multipart boundary token.
 */
export function apiRequestUploadWithProgress<T>(
  path: string,
  form: FormData,
  options: UploadWithProgressOptions = {},
): Promise<T> {
  const { accessToken, signal, onProgress } = options;
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${getConfig().pmsApiBaseUrl}${path}`);
    // The sibling-subdomain refresh cookie rides along, exactly as it does on
    // every `fetch` in this module.
    xhr.withCredentials = true;
    if (accessToken)
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

    xhr.upload.onprogress = (e) => {
      onProgress?.({
        loaded: e.loaded,
        total: e.lengthComputable ? e.total : 0,
      });
    };
    xhr.onload = () => {
      try {
        resolve(
          decodeApiBody<T>(
            xhr.status,
            xhr.status >= 200 && xhr.status < 300,
            xhr.responseText,
          ),
        );
      } catch (err) {
        reject(
          err instanceof Error
            ? err
            : new ApiError(xhr.status, "Upload failed."),
        );
      }
    };
    // A transport failure is `status: 0`, the same shape every other call in
    // this module gives a network error, so callers branch on one thing.
    xhr.onerror = () =>
      reject(
        new ApiError(0, "Network error — check your connection and try again."),
      );
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled."));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort());
    }
    xhr.send(form);
  });
}

/** As `apiRequest`, but resolves the HTTP status alongside the body. */
export async function apiRequestWithStatus<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  const { method = "GET", body, accessToken, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(`${getConfig().pmsApiBaseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // Same-site sibling subdomain (broker.* -> pms.*): the refresh cookie
      // rides along, and CORS runs with Allow-Credentials.
      credentials: "include",
      signal,
    });
  } catch {
    // DNS/offline/CORS rejection — never let it escape as an unhandled throw.
    throw new ApiError(
      0,
      "Network error — check your connection and try again.",
    );
  }

  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const message = isErrorEnvelope(data)
      ? data.error
      : `Request failed (${response.status})`;
    throw new ApiError(response.status, message, data);
  }

  return { status: response.status, data: data as T };
}
